export const OUTSTANDING_BALANCE = "outstanding_balance";
export const MIN_OVERRIDE_REASON = 10;
export const CANCEL_ROLE_REQUIRED_MESSAGE =
  "Solo un administrador o supervisor de la cuenta puede cancelar o reanudar la suscripción.";

export interface CancelBalanceCycle {
  periodId: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string | null;
  amountDue: number;
  chargeStatus: string | null;
  owedNow: boolean;
}

export interface CancelBalanceRunning {
  periodId: string;
  periodStart: string;
  periodEnd: string;
  conversations: number;
  unitPrice: number;
  minimum: number | null;
  amount: number;
}

export function runningMinimumApplies(running: CancelBalanceRunning): boolean {
  return running.minimum !== null && running.minimum > 0 &&
    Math.ceil(running.conversations * running.unitPrice) < running.minimum;
}

export type CancelMode = "now" | "at_cycle_end" | "blocked";

export interface ScheduledCancelInfo {
  cancelOn: string;
  requestedAt: string | null;
  cutOffPassed: boolean;
  debitOn: string | null;
}

export interface CancelBalance {
  today: string | null;
  billingMode: string | null;
  total: number;
  owedNowTotal: number;
  cyclesTotal: number;
  runningTotal: number;
  cycles: CancelBalanceCycle[];
  running: CancelBalanceRunning | null;
  cancelMode: CancelMode;
  cutOff: string | null;
  debitOn: string | null;
  scheduledCancel: ScheduledCancelInfo | null;
}

export interface CancelOverride {
  reason: string;
}

export interface ScheduledCancelResult {
  cancelOn: string;
  debitOn: string | null;
  estimatedAmount: number;
}

export interface PayNowResult {
  ok: boolean;
  error: string | null;
  code: string | null;
  initPoint?: string | null;
  amount?: number;
  cancelled?: boolean;
}

export interface EarlyCutOffRow {
  id: string;
  purpose: string;
  status: string;
  amount: number;
  conversations: number;
  cutOffAt: string | null;
  expiresAt: string | null;
  completedAt: string | null;
  createdAt: string | null;
  requestedByEmail: string | null;
}

export interface CancelResult {
  ok: boolean;
  error: string | null;
  code: string | null;
  scheduled?: ScheduledCancelResult | null;
  cycleCut?: CycleCutResult | null;
}

export interface CycleCutResult {
  amount: number;
  conversations: number;
}

type Raw = Record<string, unknown>;

function isRecord(value: unknown): value is Raw {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function num(value: unknown): number {
  const parsed = typeof value === "string" ? Number(value) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : 0;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function parseCycle(value: unknown): CancelBalanceCycle | null {
  if (!isRecord(value)) return null;
  const periodStart = str(value.period_start);
  const periodEnd = str(value.period_end);
  const amountDue = num(value.amount_due);
  if (!periodStart || !periodEnd || amountDue <= 0) return null;
  return {
    periodId: str(value.period_id) ?? "",
    periodStart,
    periodEnd,
    dueDate: str(value.due_date),
    amountDue,
    chargeStatus: str(value.charge_status),
    owedNow: value.owed_now === true,
  };
}

function parseRunning(value: unknown): CancelBalanceRunning | null {
  if (!isRecord(value)) return null;
  const periodStart = str(value.period_start);
  const periodEnd = str(value.period_end);
  const amount = num(value.amount);
  if (!periodStart || !periodEnd || amount <= 0) return null;
  return {
    periodId: str(value.period_id) ?? "",
    periodStart,
    periodEnd,
    conversations: num(value.conversations),
    unitPrice: num(value.unit_price),
    minimum: value.minimum === null || value.minimum === undefined ? null : num(value.minimum),
    amount,
  };
}

function parseScheduledCancel(value: unknown): ScheduledCancelInfo | null {
  if (!isRecord(value)) return null;
  const cancelOn = str(value.cancel_on);
  if (!cancelOn) return null;
  return {
    cancelOn,
    requestedAt: str(value.requested_at),
    cutOffPassed: value.cut_off_passed === true,
    debitOn: str(value.debit_on),
  };
}

function parseCancelMode(value: unknown, total: number): CancelMode {
  if (value === "now" || value === "at_cycle_end" || value === "blocked") return value;
  return total > 0 ? "blocked" : "now";
}

export function parseScheduledCancelResult(raw: unknown): ScheduledCancelResult | null {
  if (!isRecord(raw) || raw.scheduled !== true) return null;
  const cancelOn = str(raw.cancel_on);
  if (!cancelOn) return null;
  return { cancelOn, debitOn: str(raw.debit_on), estimatedAmount: num(raw.estimated_amount) };
}

export function parseCycleCutResult(raw: unknown): CycleCutResult | null {
  if (!isRecord(raw) || !isRecord(raw.cycle_cut) || raw.cycle_cut.outcome !== "cut") return null;
  return { amount: num(raw.cycle_cut.amount), conversations: num(raw.cycle_cut.conversations) };
}

export function cycleCutMessage(cut: CycleCutResult): string {
  const conversations = cut.conversations === 1 ? "1 conversación" : `${cut.conversations.toLocaleString("es-CO")} conversaciones`;
  return `Autorización cancelada. El ciclo se cerró con ${formatCop(cut.amount)} por ${conversations}; queda como saldo pendiente de pago.`;
}

export function parseCancelBalance(raw: unknown): CancelBalance | null {
  if (!isRecord(raw)) return null;
  return {
    today: str(raw.today),
    billingMode: str(raw.billing_mode),
    total: num(raw.total),
    owedNowTotal: num(raw.owed_now_total),
    cyclesTotal: num(raw.cycles_total),
    runningTotal: num(raw.running_total),
    cycles: (Array.isArray(raw.cycles) ? raw.cycles : [])
      .map(parseCycle)
      .filter((cycle): cycle is CancelBalanceCycle => cycle !== null),
    running: parseRunning(raw.running),
    cancelMode: parseCancelMode(raw.cancel_mode, num(raw.total)),
    cutOff: str(raw.cut_off),
    debitOn: str(raw.debit_on),
    scheduledCancel: parseScheduledCancel(raw.scheduled_cancel),
  };
}

export function cancelOverrideBody(override?: CancelOverride): Record<string, unknown> {
  return override ? { override: true, override_reason: override.reason.trim() } : {};
}

export function isOverrideReasonValid(reason: string): boolean {
  return reason.trim().length >= MIN_OVERRIDE_REASON;
}

export function formatCop(amount: number): string {
  return `$${Math.round(amount).toLocaleString("es-CO")} COP`;
}

export function canPayNow(balance: CancelBalance): boolean {
  if (balance.billingMode !== "per_use") return false;
  if (balance.scheduledCancel?.cutOffPassed) return false;
  if (!balance.cutOff || !balance.today) return false;
  return balance.cutOff.slice(0, 10) > balance.today.slice(0, 10);
}

export function parsePayNowResult(raw: unknown): PayNowResult {
  if (!isRecord(raw) || raw.pay_now !== true) return { ok: false, error: null, code: null };
  return {
    ok: true,
    error: null,
    code: null,
    initPoint: str(raw.init_point),
    amount: num(raw.amount),
    cancelled: raw.cancelled === true,
  };
}

export function parseEarlyCutOffs(raw: unknown): EarlyCutOffRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).map((row) => ({
    id: str(row.id) ?? "",
    purpose: str(row.purpose) ?? "cancel_now",
    status: str(row.status) ?? "pending",
    amount: num(row.amount),
    conversations: num(row.conversations),
    cutOffAt: str(row.cut_off_at),
    expiresAt: str(row.expires_at),
    completedAt: str(row.completed_at),
    createdAt: str(row.created_at),
    requestedByEmail: str(row.requested_by_email),
  }));
}

export function earlyCutOffStatus(row: EarlyCutOffRow, now: Date = new Date()): string {
  if (row.purpose === "authorization_lost") return "Autorización cancelada en Mercado Pago: ciclo cerrado con saldo pendiente";
  if (row.purpose === "authorization_cancelled") return "Autorización cancelada de inmediato por Flow: ciclo cerrado con saldo pendiente";
  if (row.purpose === "authorization_start") return "Autorización registrada: el consumo anterior se cerró sin cobro y el ciclo se alineó con el débito de Mercado Pago";
  if (row.status === "completed") return "Pagado y cancelado";
  if (row.status === "superseded") return "Reemplazado por otro intento";
  if (row.status === "expired" || (row.status === "pending" && row.expiresAt && new Date(row.expiresAt) <= now)) {
    return "Expirado sin pago";
  }
  return "Esperando el pago";
}
